import type {
    AllowedWebsite,
    IpwConfig,
    MerchantConfig,
} from './backend_config.types';
import {
    MerchantWebsiteType,
    MerchantConfigSchema,
    RpaModuleName,
} from './backend_config.types';
import {
    defaultIpgConfigById,
    defaultIpgConfigByKey,
} from './types/ipg/ipg-backend-config-registry';
export {
    defaultDemoIpgConfigByKey,
    defaultIpgConfigById,
    defaultIpgConfigByKey,
} from './types/ipg/ipg-backend-config-registry';
import {
    IpwSystemId,
    IpwSystemIdKey,
    type ThirdPartySystemId,
} from './types/third-party-system.enums';

interface DefaultLaunchWebsiteConfig {
    readonly allowedWebsites: readonly { readonly url: string }[];
    readonly name: string;
}

interface DefaultAllowedWebsiteConfig {
    readonly allowedWebsites: readonly AllowedWebsite[];
    readonly name: string;
}

interface DefaultIntegratedWebsiteNavigationConfig extends DefaultLaunchWebsiteConfig {
    readonly allowedWebsites: readonly {
        readonly allowedUrlPatterns: readonly string[];
        readonly url: string;
    }[];
    readonly urlPattern: string;
}

function requireDefaultLaunchUrl(config: DefaultLaunchWebsiteConfig): string {
    const [allowedWebsite] = config.allowedWebsites;
    if (allowedWebsite === undefined) {
        throw new Error(`${config.name} launch URL is not configured.`);
    }
    return allowedWebsite.url;
}

function requireDefaultAllowedWebsite(
    config: DefaultAllowedWebsiteConfig
): AllowedWebsite {
    const [allowedWebsite] = config.allowedWebsites;
    if (allowedWebsite === undefined) {
        throw new Error(`${config.name} allowed website is not configured.`);
    }
    return allowedWebsite;
}

function requireDefaultNavigationAllowedUrlPatterns(
    config: DefaultIntegratedWebsiteNavigationConfig
): readonly string[] {
    const patterns: Set<string> = new Set([config.urlPattern]);
    for (const allowedWebsite of config.allowedWebsites) {
        for (const allowedUrlPattern of allowedWebsite.allowedUrlPatterns) {
            patterns.add(allowedUrlPattern);
        }
    }
    if (patterns.size === 0) {
        throw new Error(`${config.name} navigation URL patterns are not configured.`);
    }
    return [...patterns];
}

export const ipwConfigPcpartpicker: IpwConfig = {
    ipw_id: IpwSystemId.PCPARTPICKER,
    ipw_system_id: IpwSystemIdKey.PCPARTPICKER,
    name: 'PCPartPicker',
    urlPattern: 'https://pcpartpicker.com/*',
    workflow_prefix: 'pcpartpicker',
    allowedWebsites: [
        {
            type: MerchantWebsiteType.IPW,
            system_id: IpwSystemId.PCPARTPICKER,
            url: 'https://pcpartpicker.com/list',
            name: 'PCPartPicker (Demo SOR)',
            allowedUrlPatterns: ['https://pcpartpicker.com/'],
            sessionPersistence: 'persistent',
            promoteSessionCookies: false,
            urlPatternsRules: [],
            fetchDefaultAction: 'allow',
            iframeUrlPatterns: [],
            iframeDefaultAction: 'allow',
        },
    ],
    rpaModules: [RpaModuleName.PCPARTPICKER_IPW],
    reconciliation: {
        paymentCode: '',
        doInjectApprovalNumber: false,
        doFinalizeReconciliation: false,
    },
};

export const ipwConfigDealertrack: IpwConfig = {
    ipw_id: IpwSystemId.DEALERTRACK,
    ipw_system_id: IpwSystemIdKey.DEALERTRACK,
    name: 'Dealertrack',
    urlPattern: 'https://*.coxautoinc.com/*',
    workflow_prefix: 'dealertrack',
    allowedWebsites: [
        {
            type: MerchantWebsiteType.IPW,
            system_id: IpwSystemId.DEALERTRACK,
            url: 'https://signin.coxautoinc.com/',
            name: 'Dealertrack',
            allowedUrlPatterns: ['https://*.coxautoinc.com/*'],
            sessionPersistence: 'persistent',
            promoteSessionCookies: true,
            urlPatternsRules: [],
            fetchDefaultAction: 'allow',
            iframeUrlPatterns: [],
            iframeDefaultAction: 'allow',
        },
    ],
    rpaModules: [RpaModuleName.DEALERTRACK_IPW],
    reconciliation: {
        paymentCode: 'AB',
        doInjectApprovalNumber: false,
        doFinalizeReconciliation: false,
    },
};

export const defaultIpwConfigByKey: Partial<Record<IpwSystemIdKey, IpwConfig>> = {
    [IpwSystemIdKey.PCPARTPICKER]: ipwConfigPcpartpicker,
    [IpwSystemIdKey.DEALERTRACK]: ipwConfigDealertrack,
};

export const defaultIpwConfigById: Partial<Record<IpwSystemId, IpwConfig>> = {
    [IpwSystemId.PCPARTPICKER]: ipwConfigPcpartpicker,
    [IpwSystemId.DEALERTRACK]: ipwConfigDealertrack,
};

export const defaultIntegratedWebsiteSystemIds = [
    ...Object.values(defaultIpgConfigById).map((config) => config.ipg_id),
    IpwSystemId.DEALERTRACK,
    IpwSystemId.PCPARTPICKER,
] as const satisfies readonly ThirdPartySystemId[];

export const defaultIntegratedWebsiteDisplayNameBySystemId: Partial<
    Record<ThirdPartySystemId, string>
> = {
    [IpwSystemId.GENERIC]: 'RPA Builder',
    ...Object.fromEntries(
        Object.values(defaultIpgConfigById).map((config) => [
            config.ipg_id,
            config.name,
        ])
    ),
    [IpwSystemId.DEALERTRACK]: ipwConfigDealertrack.name,
    [IpwSystemId.PCPARTPICKER]: ipwConfigPcpartpicker.name,
};

export const defaultIntegratedWebsiteLaunchUrlBySystemId: Partial<
    Record<ThirdPartySystemId, string>
> = {
    ...Object.fromEntries(
        Object.values(defaultIpgConfigById).map((config) => [
            config.ipg_id,
            requireDefaultLaunchUrl(config),
        ])
    ),
    [IpwSystemId.DEALERTRACK]: requireDefaultLaunchUrl(ipwConfigDealertrack),
    [IpwSystemId.PCPARTPICKER]: requireDefaultLaunchUrl(ipwConfigPcpartpicker),
};

export const defaultIntegratedWebsiteAllowedWebsiteBySystemId: Partial<
    Record<ThirdPartySystemId, AllowedWebsite>
> = {
    ...Object.fromEntries(
        Object.values(defaultIpgConfigById).map((config) => [
            config.ipg_id,
            requireDefaultAllowedWebsite(config),
        ])
    ),
    [IpwSystemId.DEALERTRACK]:
        requireDefaultAllowedWebsite(ipwConfigDealertrack),
    [IpwSystemId.PCPARTPICKER]:
        requireDefaultAllowedWebsite(ipwConfigPcpartpicker),
};

export const defaultIntegratedWebsiteAllowedUrlPatternsBySystemId: Partial<
    Record<ThirdPartySystemId, readonly string[]>
> = {
    ...Object.fromEntries(
        Object.values(defaultIpgConfigById).map((config) => [
            config.ipg_id,
            requireDefaultNavigationAllowedUrlPatterns(config),
        ])
    ),
    [IpwSystemId.DEALERTRACK]:
        requireDefaultNavigationAllowedUrlPatterns(ipwConfigDealertrack),
    [IpwSystemId.PCPARTPICKER]:
        requireDefaultNavigationAllowedUrlPatterns(ipwConfigPcpartpicker),
};

export const defaultIpxCredentialLaunchUrlBySystemId =
    defaultIntegratedWebsiteLaunchUrlBySystemId;

export const defaultMerchantConfig: MerchantConfig = MerchantConfigSchema.parse({
    ipg: defaultIpgConfigByKey,
    ipw: defaultIpwConfigByKey,
});
